import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProcInstanceContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import {
  useStartInstanceStepConversation,
  useGetInstanceStepAiMessages,
  SendInstanceStepAiMessageRequest,
} from '@/shared/api';
import { useHotkey } from '@/shared/hooks/useHotkey';
import { ChatMode } from '@/shared/types';
import { captureError } from '@/shared/utils';
import { aiConversationHistoryResponseToMessages } from '../api/mappers';
import { ChatMessageType } from '../model/types';

type PromptType = SendInstanceStepAiMessageRequest['promptType'];

type Props = {
  stepId: string;
  stepThreadId: string;
  chatOpen?: boolean;
  chatMode: ChatMode | undefined;
  setIsOpenAIKeyDeleted: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useInstanceAiConversation = ({
  stepThreadId,
  stepId,
  chatOpen,
  chatMode,
  setIsOpenAIKeyDeleted,
  setIsLoading,
}: Props) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { procInstance } = useProcInstanceContext();

  const [promptType, setPromptType] = useState<PromptType>('BASIC');
  const [threadId, setThreadId] = useState<string>(stepThreadId);
  const [response, setResponse] = useState('');
  const [waitingToStartGenerating, setWaitingToStartGenerating] =
    useState(false);

  const togglePromptType = useCallback(() => {
    if (chatOpen) {
      const newPromptType = promptType === 'BASIC' ? 'ADVANCED' : 'BASIC';
      setPromptType(newPromptType);
      showNotification({
        type: 'info',
        message: `Prompt updated type changed to ${newPromptType}`,
        description:
          'Prompt type determines how the AI will respond to your messages. Basic is more concise, while Advanced provides more context and detail.',
      });
    }
  }, [chatOpen, promptType, showNotification]);
  useHotkey({ code: 'KeyK', altKey: true, shiftKey: true }, togglePromptType);

  const { mutate: startConversation } = useStartInstanceStepConversation();
  const {
    data = [],
    isLoading: aiConversationLoading,
    refetch: refetchMessages,
  } = useGetInstanceStepAiMessages(
    {
      pathParams: {
        instanceId: procInstance?.id ?? '',
        threadId: threadId ?? '',
      },
      queryParams: {
        procId: procInstance?.parentProcId ?? '',
      },
    },
    {
      enabled:
        Boolean(threadId) &&
        Boolean(procInstance?.parentProcId) &&
        chatMode !== ChatMode.Preview,
      onSuccess: () => {
        setResponse('');
      },
      onError: (error) => {
        if (error.status === 404) {
          setThreadId('');
        }
        captureError(error, {
          module: 'ai_chat',
          action: 'get_messages',
        });
      },
    },
  );

  const sendMessageToAi = useCallback(
    async (message: string) => {
      if (!threadId) {
        return;
      }
      setWaitingToStartGenerating(true);
      setIsLoading(true);

      // show user message immediately
      // /:instanceId/threads/:threadId/messages
      queryClient.setQueryData(
        [
          'instances',
          procInstance?.id,
          'threads',
          threadId,
          'messages',
          { procId: procInstance?.parentProcId },
        ],
        (oldData) => [
          ...(Array.isArray(oldData) ? oldData : []),
          {
            id: new Date().toISOString(),
            content: message,
            createdAt: new Date().toISOString(),
            role: ChatMessageType.User,
          },
        ],
      );

      try {
        await fetch(
          `${
            import.meta.env.VITE_API_BASE_URL
          }/instances/${procInstance?.id}/threads/${threadId}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-type': 'application/json',
            },
            body: JSON.stringify({
              message,
              procId: procInstance?.parentProcId,
              stepId,
              promptType,
            }),
          },
        ).then(async (res) => {
          if (!res.ok) {
            const error = await res.json();
            showNotification({
              type: 'error',
              message: 'Error sending message',
              description: error.stack?.message || error.message,
            });
            setIsLoading(false);
            setWaitingToStartGenerating(false);
            return;
          }

          if (res.body === null) {
            throw new Error('Send message response body is null');
          }

          // https://developer.mozilla.org/en-US/docs/Web/API/ReadableStream/getReader
          const reader = res.body.getReader();

          const processStream = async () => {
            setWaitingToStartGenerating(false);
            while (reader) {
              // .read() returns 2 properties
              const { done, value } = await reader.read();
              // if done is true
              if (done) {
                refetchMessages();
                setIsLoading(false);
                break;
              }
              // value is a binary data in Uint8Array format, as Uint8Array is suitable data structure for binary data
              // we decode Uint8Array using TextDecoder
              const chunk = new TextDecoder('utf-8').decode(value);

              // append to the response
              setResponse((prev) => prev + chunk);
            }
          };

          processStream().catch((error) => {
            captureError(error, {
              module: 'ai_chat',
              action: 'process_stream',
            });

            showNotification({
              type: 'error',
              message: 'Error sending message',
              description: error.stack?.message || error.message,
            });
            setIsLoading(false);
          });
        });
      } catch (error) {
        captureError(error, {
          module: 'ai_chat',
          action: 'send_message',
        });
      }
    },
    [
      procInstance?.id,
      procInstance?.parentProcId,
      promptType,
      queryClient,
      refetchMessages,
      setIsLoading,
      showNotification,
      stepId,
      threadId,
    ],
  );

  useEffect(() => {
    if (chatOpen && procInstance?.organizationId && !threadId) {
      startConversation(
        { pathParams: { instanceId: procInstance?.id }, body: { stepId } },
        {
          // TODO
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onSuccess: (data: any) => {
            setThreadId(data.id);
            setIsOpenAIKeyDeleted(false);
          },
          // TODO
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            if (error.stack.code === 'invalid_api_key') {
              setIsOpenAIKeyDeleted(true);
            }
            showNotification({
              type: 'error',
              message: 'Error loading AI chat',
              description: error.stack?.message || error.message,
            });

            captureError(error, {
              module: 'ai_chat',
              action: 'start_conversation',
            });
          },
        },
      );
    }
  }, [
    procInstance?.id,
    procInstance?.organizationId,
    procInstance?.parentProcId,
    setIsOpenAIKeyDeleted,
    showNotification,
    startConversation,
    stepId,
    threadId,
    chatOpen,
  ]);

  const isMyMessage = (createdBy: ChatMessageType) =>
    createdBy !== ChatMessageType.Assistant;

  return {
    threadId,
    aiConversationMessages: aiConversationHistoryResponseToMessages(data),
    aiConversationLoading,
    sendMessageToAi,
    waitingToStartGenerating,
    streamedMessage: response
      ? {
          id: 'streamed-message',
          created: {
            at: new Date().toISOString(),
            by: { type: ChatMessageType.Assistant },
          },
          text: response,
        }
      : null,
    isMyMessageInAiConversation: isMyMessage,
  };
};
